import { apiClient, ApiError } from './client';
import type { components } from './schema';
export type AiRequest = components['schemas']['AiActionRequest'];
export type AiEnvelope = components['schemas']['AiEnvelope'];
export type AiScope = AiRequest['scopeType'];
export type AiOption = components['schemas']['AiRecordOption'];
export type AiChatMessage = components['schemas']['AiChatTurn'];
export type AiChatConversation = components['schemas']['AiChatConversation'];
export type AiProposal = components['schemas']['AiProposedActionView'];
const GENERIC_FAILURE = 'The assistant request could not be completed.';

/** The server's own explanation from an RFC 9457 problem body, when it sent one. */
function problemMessage(problem: unknown): string {
  if (typeof problem !== 'object' || problem === null) return GENERIC_FAILURE;
  for (const field of ['detail', 'title'] as const) {
    const value: unknown = field in problem ? (problem as Record<string, unknown>)[field] : null;
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return GENERIC_FAILURE;
}

function result<T>(r: { data?: T; response: Response; error?: unknown }): T {
  if (!r.response.ok || r.data === undefined)
    throw new ApiError(r.response.status, problemMessage(r.error));
  return r.data;
}
export async function assistantCapabilities() {
  return result(await apiClient.GET('/api/assistant/capabilities'));
}
export async function assistantRecords(
  type: AiScope,
  query: string,
  page: number,
  customerId?: string,
) {
  return result(
    await apiClient.GET('/api/assistant/records', {
      params: { query: { type, query, page, ...(customerId ? { customerId } : {}) } },
    }),
  );
}
export async function summarizeRecord(body: AiRequest) {
  return result(await apiClient.POST('/api/assistant/requests', { body }));
}

export async function generalAssistantChat(body: components['schemas']['AiChatRequest']) {
  return result(await apiClient.POST('/api/assistant/chat', { body }));
}

export async function generalAssistantConversation(id: string) {
  return result(
    await apiClient.GET('/api/assistant/conversations/{id}', { params: { path: { id } } }),
  );
}

export async function readAiProposal(id: string) {
  return result(await apiClient.GET('/api/assistant/proposals/{id}', { params: { path: { id } } }));
}

/**
 * Approves the exact draft a person read and applies it, as one attested act.
 *
 * The server does both or neither: if the record the draft was written from has moved, nothing is
 * saved and the draft is still waiting. The digest and version are those of the draft on screen,
 * and `attested` is the person's statement that they read it, shown beside the button.
 *
 * @param proposal the draft as the person saw it
 * @returns the applied proposal
 */
export async function approveAndApplyAiProposal(proposal: AiProposal) {
  return result(
    await apiClient.POST('/api/assistant/proposals/{id}/approve-and-apply', {
      params: { path: { id: proposal.id } },
      body: {
        expectedVersion: proposal.version,
        payloadSha256: proposal.payloadSha256,
        attested: true,
      },
    }),
  );
}
export async function decideAiProposal(proposal: AiProposal, approve: boolean) {
  return result(
    await apiClient.POST('/api/assistant/proposals/{id}/decision', {
      params: { path: { id: proposal.id } },
      body: { expectedVersion: proposal.version, payloadSha256: proposal.payloadSha256, approve },
    }),
  );
}
export async function applyAiProposal(proposal: AiProposal) {
  return result(
    await apiClient.POST('/api/assistant/proposals/{id}/apply', {
      params: { path: { id: proposal.id } },
      body: { expectedVersion: proposal.version, payloadSha256: proposal.payloadSha256 },
    }),
  );
}
